<template>
  <MultiLevelNavigationModal
    v-model="localValue"
    :modal-title="modalTitle"
    :fetch-branches="getAttributeGroups"
    :fetch-leaves="getAttributes"
    :selectable-type="['leaf']"
    :selected-entry-id="selectedEntryId"
    :selected-path="selectedPath"
    :base-path-title="basePathTitle"
    with-all
    @confirm="onConfirm"
    @hide="emits('hide')"
    @cancel="emits('cancel')"
    @close="emits('close')" />
</template>

<script setup lang="ts">
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { Attribute, AttributeGroup } from "@medapro-types/common/common";
import { Branch, Leaf, MultiLevelNavigationConfirmModel } from "@medapro-types/common/components/MultiLevelNavigation";
import articleApi from "@services/article.js";
import attributesApi from "@services/attributes.js";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import MultiLevelNavigationModal from "./MultiLevelNavigationModal.vue";
import { BaseIds } from "@util/enums/base-ids";

const BRANCH_ICON = "AttributeGroup1";
const LEAF_ICON = "Attribute";

//#region General
const emits = defineEmits(["confirm", "cancel", "close", "input", "hide"]);

const props = defineProps<{
  value: boolean;
  attributeType: "table" | "text";
  selectedEntryId: string | null | undefined;
  selectedPath: Branch[] | null | undefined;
}>();

const { t: $t } = useI18n();

const modalTitle = computed(() => {
  switch (props.attributeType) {
    case "text":
      return $t("selectTextAttribute");
    case "table":
      return $t("selectTableAttribute");
    default:
      return "";
  }
});

const basePathTitle = computed(() => {
  switch (props.attributeType) {
    case "text":
      return $t("text.attributes");
    case "table":
      return $t("table.attributes");
    default:
      return "";
  }
});
//#endregion

//#region Store

const { primaryLanguageCode } = useContentLanguages();

//#endregion

const onConfirm = (event: MultiLevelNavigationConfirmModel) => {
  emits("confirm", event);
};

//#region CONSTANTS
const BASE_ATTRIBUTE_GROUP_ID = BaseIds.BaseAttributeGroup;
//#endregion

//#region localValue
const localValue = computed({
  get: () => props.value,
  set: (nv) => emits("input", nv)
});

//#endregion

//#region FetchMethods
const getAttributeGroups = async (groupId: string | null | undefined): Promise<Branch[]> => {
  const fetchResult: AttributeGroup[] = await articleApi.getAttributeGroups({
    language: primaryLanguageCode.value,
    id: groupId ?? BASE_ATTRIBUTE_GROUP_ID
  });
  return fetchResult.map((x) => ({ id: x.attributeGroupId, depth: x.depth, name: x.name, parentId: x.parentId, icon: BRANCH_ICON }));
};

const getAttributes = async (groupId: string | null | undefined): Promise<Leaf[]> => {
  const consideredGroupId = groupId ?? BASE_ATTRIBUTE_GROUP_ID;
  const fetchResult: Record<string, Attribute>[] = await attributesApi.get([primaryLanguageCode.value], consideredGroupId, props.attributeType == "text");

  return fetchResult
    .filter((x) => x[primaryLanguageCode.value].attributeGroupId == consideredGroupId)
    .map((x) => ({
      id: x[primaryLanguageCode.value].attributeId,
      name: x[primaryLanguageCode.value].name,
      dataType: x[primaryLanguageCode.value].dataType,
      icon: LEAF_ICON
    }));
};
//#endregion
</script>
